/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import Cgg from '.';


export const getCodeBlocks = (feature: Cgg): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project: ProjectConfiguration = feature.project;
    if (!feature.getEnabled()) {
        return blocks;
    }

    const INTERFACE = 'current_interface';
    const TABLES = feature.getSettingValue('TABLES');
    const SERVER = feature.getSettingValue('SERVER');
    const SERVER_PORT = 8083;
    const settings = [];
    const CGG_MAX_SETTING_NAME_LENGTH = 0;
    const USERNAME = feature.getSettingValue('USERNAME');
    const PASSWORD = feature.getSettingValue('PASSWORD');
    const GROUPS = feature.getSettingValue('GROUPS');
    const DATA_TABLES = project.getFeature('tbl');
    const logEnabled = project.getSettingValue('log', 'ENABLED');
    const TABLES_ENABLED = DATA_TABLES && DATA_TABLES.getEnabled();
    const COMMANDSFeature = project.getFeature('commands');
    const appVersion = project.getSettingValue('general', 'FIRMWAREVERSION');
    const TLS_ENABLED = feature.getSettingValue('TLS', true) === '1';
    const TIMEZONE = project.getSettingValue('general', 'TIMEZONE');
    const LOGGING = project.getSettingValue('general', 'LOGGING');

    let commands = [];
    if (COMMANDSFeature) {
        commands = COMMANDSFeature.getSettingValue('COMMANDS');
    }

    let variablesCode = '';
    let variableSetCode = '';
    // let tablesSendCode = '';
    // let commandsCode = '';
    // let variableGetCode = '';
    const variablesGetCode = [];
    // let cmdExecCode = '';
    const variables = project.getVariables();
    const widgets = feature.getSettingValue('WIDGETS');
    const widgetsArray = [];

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
const cggConsole = {
    /****CGG_CONSOLE_SETUP_WIDGETS****/
    "settings":[
        /****CGG_SETTINGS_SCHEMA****/
    ],
    /****CGG_APP_VERSION****/
    "tables": [
        /****CGG_CONSOLE_TABLES****/
    ],
    /****CGG_SETUP_TITLE****/
}
`,
    });

    blocks.push({
        location: 'CGG_SETUP_TITLE',
        blockType: BlockType.LOCATION,
        template: `
"title": "Device Console"`,
    });

    for (let i = 0; i < TABLES.length; i++) {
        const tblItem = project.getFeature(TABLES[i].getSettingValue('TABLE')) as TableItem;
        if (!tblItem) {
            continue;
        }
        const tableJson = Tbl.getTableJson(tblItem);
        blocks.push({
            location: 'CGG_CONSOLE_TABLES',
            blockType: BlockType.CODE,
            template: `
            ${JSON.stringify(tableJson)},`,
        });
    }

    const variableUpdateCodes: { [name: string]: string } = {};
    const enabledVariables = feature.getVariables();
    for (let i = 0; i < variables.length; i++) {
        const variable = variables[i];
        const variableName = variable.getSettingValue('VARNAME');
        if (!enabledVariables.includes(variableName)) {
            continue;
        }
        let variableDatatype = variable.getSettingValue('DATATYPE');
        if (variableDatatype === TibboDataType.DATETIME) {
            variableDatatype = TibboDataType.DWORD;
        }
        if (variableDatatype === TibboDataType.TIME) {
            variableDatatype = TibboDataType.FLOAT;
        }
        // const variableValue = project.convertDataType(
        //     variableName, variableDatatype, TibboDataType.STRING,
        // );
        const variableValue = variableName;
        let variableString = `"+${variableValue}+"`;
        if (variableDatatype === TibboDataType.STRING) {
            variableString = `\\x22"+${variableValue}+"\\x22`;
        }
        const variableUpdateCode = `simulator.cggHandler.publishVariable("${variableName}", ${variableValue})`;
        variablesCode += variableUpdateCode;
        variableUpdateCodes[variableName] = variableUpdateCode;

        blocks.push({
            location: `var_${variableName}_changed`,
            blockType: BlockType.CODE,
            template: `
if (cgg_connected) {
    ${variableUpdateCode}
}
            `,
        });

        blocks.push({
            location: 'CGG_PROC_TIMER',
            blockType: BlockType.CODE,
            template: `
${variableUpdateCode}`,
        });


        variableSetCode += `
        if (variable_name == "${variableName}") {
            value=${project.convertDataType('value', TibboDataType.STRING, variableDatatype)}
            var_${variableName}_set(value);
            ${variableUpdateCode}
        }
        `;
    }

    let tablesSendCode = '';
    if (DATA_TABLES && DATA_TABLES.getEnabled()) {
        const TABLES = DATA_TABLES.getSettingValue('TABLES');
        for (let i = 0; i < TABLES.length; i++) {
            const table = TABLES[i];
            if (table.getSettingValue('STRUCT') === 'L') {
                continue;
            }
            const tableName = TABLES[i].getSettingValue('NAME');
            tablesSendCode += `
    cgg_send_table("${tableName}")
                    `;
        }
    }

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
function cgg_send_tables() {
    ${tablesSendCode}
    cgg_tbl_changed=false;
}
            `,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
function cgg_send_table(table_name) {
    const topic = \`devices/\${simulator.cggHandler.deviceId}/state/tables/\${table_name}\`;
    const tableData = simulator.tables[table_name];
    const fields = tablesSchema[table_name].fields.map(field => field.name);
    const header = ["index", ...fields].join(',');
    const payload = [header, ...tableData.map((row, index) => [index, ...row].join(','))].join('\\r\\n');
    simulator.cggHandler.publish(topic, payload);
}
            `,
    });

    let commandsCode = '';
    for (let i = 0; i < commands.length; i++) {
        const command = commands[i];
        const commandName = command.getSettingValue('NAME');
        commandsCode += `
            if (cmd == "${commandName}") {
                cmd_${commandName}(cmd_data);
            }`;
    }

    if (widgets.length > 0) {
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i].getWidgetObject();
            let { variable, linkType } = widget;
            try {
                const item = project.getFeature(variable);
                if (item?.id === 'variable') {
                    variable = item.getSettingValue('VARNAME');
                    linkType = 'variable';
                } else if (item?.id === 'command') {
                    variable = item.getSettingValue('NAME');
                    linkType = 'command';
                }
                widget.variable = variable;
                widget.linkType = linkType;
                widgetsArray.push(widget);
            } catch (ex) {
                widgets.splice(i, 1);
                i--;
            }
        }
    }

    blocks.push({
        location: 'CGG_CONSOLE_SETUP_WIDGETS',
        blockType: BlockType.CODE,
        template: `
        "updateInterval": ${feature.getSettingValue('INTERVAL') * 1000},
        "widgets": ${JSON.stringify(widgetsArray)},`,
    });

    blocks.push({
        location: 'CGG_APP_VERSION',
        blockType: BlockType.CODE,
        template: `
        "appName": "${project.name}",
        "appVersion": "${appVersion}",
        "appId": "${project.id}",
        `,
    });

    blocks.push({
        location: 'callback_mqtt_connect_ok',
        blockType: BlockType.CODE,
        template: `
simulator.cggHandler.publish(\`devices/\${${USERNAME}}/state/timezone\`, ${TIMEZONE});
cgg_connected=true;
if (cggConsole.tables.length > 0) {
    cgg_send_tables();
}
`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
let cgg_connected=false;
let cgg_vars_sent=false;
let cgg_tbl_changed=false;
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
this.cggHandler=new CggHandler(${SERVER}, ${SERVER_PORT}, ${USERNAME}, ${PASSWORD}, callback_mqtt_notif, callback_mqtt_connect_ok)
this.cggHandler.connect()
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
function CggHandler(server, port, deviceId, devicePassword, callback, onConnectCallback) {
    this.deviceId = deviceId;

    this.devicePassword = devicePassword;

    this.server = server;

    this.port = port;

    this.tls = false;

    this.callback = callback;

    this.onConnectCallback = onConnectCallback;

    this.client = null;

    this.connect = () => {
        this.client = mqtt.connect(\`ws://\${this.server}:\${this.port}\`, {
            username: this.deviceId,
            clientId: this.deviceId,
            password: this.devicePassword,
            keepalive: 30,
        });

        this.client.on('connect', this.handleConnect);
        this.client.on('message', (topic, message) => {
            this.execute(topic, message.toString());
        });
        // Nothing needed here because mqtt.connect() already starts connection.
    };

    this.handleConnect = async () => {
            
        const prefix = \`devices/\${this.deviceId}\`;
        
        this.client.subscribe([
            \`\${prefix}/state/variables/+/set\`,
            \`\${prefix}/state/tables/+/changes\`,
            \`\${prefix}/state/tables/+/import\`,
            \`\${prefix}/commands/#\`,
            \`\${prefix}/firmware\`,
            \`\${prefix}/system/#\`,
            ]);
        
        await this.publish(\`\${prefix}/config\`, JSON.stringify(cggConsole));
        await this.publish(\`\${prefix}/state/appName\`, cggConsole.appName.toString());
        await this.publish(\`\${prefix}/state/appVersion\`, cggConsole.appVersion.toString());
        await this.publish(\`\${prefix}/state/appId\`, cggConsole.appId.toString());

        if (this.onConnectCallback) {
            this.onConnectCallback();
        }

        if (cggConsole.settings.length > 0) {
            for (const setting of cggConsole.settings) {
                if (setting.type === 'item' && settingsSchema[setting.variable] !== undefined) {
                    await this.publishVariable(setting.variable, simulator.settings[setting.variable]);
                }
            }
        }
    };

    this.execute = async (topic, msg) => {
        if (this.callback) {
            await this.callback(topic, msg);
        }
    };

    this.publishVariable = async (variable, value) => {
        const payload = { value };
        const topic = \`devices/\${this.deviceId}/state/variables/\${variable}\`;
        await this.publish(topic, JSON.stringify(payload));
    };

    this.publish = (topic, message) => {
        this.client.publish(topic, message);
    };
}
        `,
    });

    blocks.push({
        location: 'callback_mqtt_notif',
        blockType: BlockType.CODE,
        template: `
const parseRow = (table, row) => {
    row = row.replace(/"/g, '').split(',');
    const schema = cggConsole.tables.find(tableSchema => tableSchema.name === table);
    const fields = schema.fields;
    const newRow = [];
    for (let i = 0; i < fields.length; i++) {
        switch (fields[i].fieldType) {
            case 'F':
                newRow.push(Number.parseFloat(row[i]));
                break;
            default:
                newRow.push(row[i]);
                break;
        }
    }
    return newRow;
};
const parts = topic.split('/');
if (parts.length < 3) {
    return;
}
if (parts[2] == 'state' && parts[3] == 'variables') {
    const variable_name = parts[4];
    const message = JSON.parse(data);
    let value = message['data'];
    ${variableSetCode}
} else if (parts[2] == 'state' && parts[3] == 'tables') {
    if (parts[5] === 'changes') {
        const { action, table, row, data } = JSON.parse(data);
        switch (action) {
            case 'add':
                tbl_web_add_row(table, parseRow(table, data));
                break;
            case 'remove':
                tbl_web_delete_row(table, row);
                break;
            case 'update':
                tbl_web_edit_row(table, row, parseRow(table, data));
                break;
            case 'clear':
                tbl_web_clear_table(table);
                break;
            default:
                break;
        }
        simulator.cggHandler.publish(\`devices/\${simulator.cggHandler.deviceId}/state/tables/\${table}/changes\`, "");
        cgg_tbl_changed=true;
    } else if (parts[5] === 'import') {
        const table = parts[4];
        if (msg === '') {
            return;
        }
        const data = msg.split('\\r\\n');
        simulator.tables[table] = [];
        for (let i = 1; i < data.length - 1; i++) {
            tbl_web_add_row(table, parseRow(table, data[i]));
        }
        simulator.cggHandler.publish(\`devices/\${simulator.cggHandler.deviceId}/state/tables/\${table}/import\`, "");
        cgg_tbl_changed=true;
    }
} else if (parts[2] == 'commands') {
    if (parts[3] == 'I') {
        stg_restore_multiple();
        cgg_vars_sent=false;
    } else if (parts[3] == 'E') {
        simulator.reboot();
    } else {
        const cmd = parts[3];
        const message = JSON.parse(data);
        const cmd_data = message['data'];
        ${commandsCode}
    }
} else if (parts[2] == 'firmware') {
} else if (parts[2] == 'system') {
}
`,
    });


    let settingsCode = '';
    for (let i = 0; i < GROUPS.length; i++) {
        const tmpBlocks = GROUPS[i].getCodeBlocks();
        for (let j = 0; j < tmpBlocks.length; j++) {
            if (tmpBlocks[j].location === 'LUIS_GROUP_ITEM') {
                settingsCode += tmpBlocks[j].template;
                if (i + 1 < GROUPS.length) {
                    settingsCode += ',';
                }
            }
        }
    }

    blocks.push({
        location: 'CGG_SETTINGS_SCHEMA',
        template: settingsCode,
    });

    blocks.push({
        location: 'var_poll_timer_elapsed',
        blockType: BlockType.CODE,
        template: `
cgg_proc_timer();
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
function cgg_proc_timer() {
    const sendLogs = (tableName, eventType) => {
        if (!simulator.tables[tableName]) {
            return;
        }
        const logTable = simulator.tables[tableName];
        for (let i = 0; i < logTable.length; i++) {
            let [ts, data] = logTable[i];
            if (eventType === 'event') {
                const dataArray = data.split(';');
                data = {};
                for (let j = 0; j < dataArray.length - 1; j++) {
                    const [key, value] = dataArray[j].split('=');
                    data[key] = value;
                }
            }
            const payload = {
                createdAt: ts,
                data,
                type: eventType,
            }
            simulator.cggHandler.publish(\`devices/\${simulator.cggHandler.deviceId}/logs\`, JSON.stringify(payload))
            tbl_web_delete_row(tableName, i);
        }
    }
    if (cgg_tbl_changed) {
        cgg_send_tables();
    }
    if (cgg_vars_sent==false && cgg_connected==true) {
        /****CGG_PROC_TIMER****/
        cgg_vars_sent=true
    }
    ${logEnabled ? `
        sendLogs('LOG', 'event');
        sendLogs('DEBUG', 'debug');
        ` : ''}
}
        `,
    });
    if (LOGGING === '"tbl_log"') {
        blocks.push({
            location: 'print_overrides',
            blockType: BlockType.LOCATION,
            template: `
if (cgg_connected) {
    log_str = " ".join(map(str, args))
    curr_dt = datetime_current_timestamp()
    payload={
        'createdAt': curr_dt,
        'data': log_str,
        'type': 'debug',
    }
    simulator.cggHandler.publish(\`devices/\${${USERNAME}}/logs\`, JSON.stringify(payload))
}
`,
        });
    }

    blocks.push({
        location: 'callback_tbl_modified',
        blockType: BlockType.LOCATION,
        template: `
    cgg_tbl_changed=true;
    `,
    });

    return blocks;
};
